import { useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  Users,
  Layout,
  Smartphone,
  Component,
  MousePointer2,
  Palette,
  PenTool,
  Figma,
  Layers,
  Eye,
  ArrowRight,
  ArrowUpRight,
} from "lucide-react";
import {
  RevealSection,
  SplitText,
  MagneticButton,
  staggerContainer,
  staggerItem,
  useParallax,
  EASE,
} from "../components/motion/ScrollFX";

// ================= THEME =================
const theme = {
  ink: "#221619",
  inkMuted: "#6B6265",
  accent: "#E63946",
  accentDeep: "#B22234",
  gold: "#D9A441",
  border: "#E6DFDC",
  surface: "#FBF8F6",
  surfaceAlt: "#F2ECE9",
  heroBg: "#150B0D",
  darkBand: "#221619",
  darkBandDeep: "#160B0D",
  ctaBg: "#2B1013",
  ctaBgDeep: "#160A0C",
};

// ================= DATA =================
const stats = [
  { n: "200+", t: "Screens Designed" },
  { n: "80+", t: "Happy Clients" },
  { n: "5+ Years", t: "Design Experience" },
  { n: "98%", t: "User Satisfaction" },
];

const services = [
  { no: "01", icon: Users, title: "User Research & Personas", desc: "Understanding real users and their goals before a single pixel is placed." },
  { no: "02", icon: Layout, title: "Wireframes & Prototypes", desc: "Structuring layouts and interactive flows to validate ideas early." },
  { no: "03", icon: Smartphone, title: "Mobile & Web UI Design", desc: "Modern, scalable interfaces designed for every screen size." },
  { no: "04", icon: Component, title: "Design Systems & UI Kits", desc: "Reusable component libraries that keep products consistent as they grow." },
];

const serviceAccents = ["#E63946", "#D9A441", "#E63946", "#D9A441"];

const tools = ["Figma", "Adobe XD", "Sketch", "Photoshop", "Illustrator", "Framer"];

const process = [
  { step: "Research", desc: "Understanding users & goals" },
  { step: "Wireframe", desc: "Structuring layouts" },
  { step: "Visual Design", desc: "High-fidelity UI" },
  { step: "Testing", desc: "Usability & improvements" },
];

const faqs = [
  { q: "Do you follow UX research?", a: "Yes — every engagement starts with research to ground design decisions in real user needs." },
  { q: "Can you redesign existing apps?", a: "Absolutely. We audit the current experience and rebuild it around what's actually working and what isn't." },
  { q: "Do you provide design systems?", a: "Yes, our UI/UX services cover the complete product design lifecycle, including reusable design systems." },
];

function Eyebrow({ children, center = false, light = false }) {
  return (
    <div
      className={`inline-flex items-center gap-2 text-xs font-semibold tracking-[0.2em] uppercase mb-4 ${center ? "mx-auto" : ""}`}
      style={{ color: light ? "#FF8A93" : theme.accent }}
    >
      <span className="h-px w-6" style={{ backgroundColor: light ? "#FF8A93" : theme.accent }} />
      {children}
    </div>
  );
}

function DotGrid({ className = "", light = false }) {
  return (
    <div
      className={`pointer-events-none absolute inset-0 ${light ? "opacity-[0.05]" : "opacity-[0.06]"} ${className}`}
      style={{
        backgroundImage: `radial-gradient(${light ? "#FFFFFF" : theme.ink} 1px, transparent 1px)`,
        backgroundSize: "26px 26px",
      }}
    />
  );
}

// ---------------------------------------------------------------------
// Hero background — DesignCanvasField: a scattering of morphing shape
// outlines (circle/square/triangle) that slowly rotate and drift like
// objects on a design canvas, faint ruler tick-marks along the edges,
// a wandering cursor dot with a trailing "click" pulse, and drifting
// glow blobs. Reads as "design tool workspace" — distinct from Web
// Development's code-rain and Mobile App's signal pings. No card or
// device mock.
// ---------------------------------------------------------------------
const canvasShapes = [
  { type: "circle", x: 12, y: 24, size: 70, tint: theme.accent, dur: 9 },
  { type: "square", x: 86, y: 18, size: 46, tint: theme.gold, dur: 11 },
  { type: "triangle", x: 8, y: 74, size: 54, tint: theme.gold, dur: 8 },
  { type: "circle", x: 90, y: 70, size: 40, tint: theme.accent, dur: 10 },
  { type: "square", x: 50, y: 88, size: 30, tint: "#FFFFFF", dur: 7 },
  { type: "circle", x: 46, y: 10, size: 26, tint: "#FFFFFF", dur: 6 },
];

function ShapeOutline({ type, size, tint }) {
  const stroke = tint;
  if (type === "circle") {
    return (
      <svg width={size} height={size} viewBox="0 0 100 100" fill="none">
        <circle cx="50" cy="50" r="46" stroke={stroke} strokeWidth="2.5" strokeDasharray="6 8" opacity="0.55" />
      </svg>
    );
  }
  if (type === "square") {
    return (
      <svg width={size} height={size} viewBox="0 0 100 100" fill="none">
        <rect x="6" y="6" width="88" height="88" rx="14" stroke={stroke} strokeWidth="2.5" strokeDasharray="6 8" opacity="0.55" />
      </svg>
    );
  }
  return (
    <svg width={size} height={size} viewBox="0 0 100 100" fill="none">
      <polygon points="50,6 94,90 6,90" stroke={stroke} strokeWidth="2.5" strokeDasharray="6 8" opacity="0.55" strokeLinejoin="round" />
    </svg>
  );
}

function DesignCanvasField() {
  return (
    <div className="pointer-events-none absolute inset-0 overflow-hidden">
      <span className="hero-glow-u-a" />
      <span className="hero-glow-u-b" />

      {canvasShapes.map((s, i) => (
        <div
          key={i}
          className="shape-drift absolute"
          style={{
            left: `${s.x}%`,
            top: `${s.y}%`,
            animationDuration: `${s.dur}s`,
            animationDelay: `${i * 0.5}s`,
          }}
        >
          <ShapeOutline type={s.type} size={s.size} tint={s.tint} />
        </div>
      ))}

      {/* wandering cursor with click-pulse */}
      <div className="cursor-wander absolute">
        <MousePointer2 size={20} color="#FFFFFF" fill={theme.accent} style={{ opacity: 0.85 }} />
        <span className="cursor-pulse absolute rounded-full" style={{ borderColor: theme.accent }} />
      </div>

      {/* faint ruler ticks along top edge */}
      <div className="absolute top-0 left-0 right-0 h-6 flex items-end opacity-20">
        {Array.from({ length: 40 }).map((_, i) => (
          <span
            key={i}
            className="flex-1"
            style={{
              height: i % 5 === 0 ? "12px" : "6px",
              borderRight: "1px solid rgba(255,255,255,0.4)",
            }}
          />
        ))}
      </div>

      <span className="hero-dots-u" />
      <span className="hero-vignette-u" />
    </div>
  );
}

export default function UIUX() {
  const [activeService, setActiveService] = useState(0);
  const heroRef = useRef(null);
  const bgParallax = useParallax(heroRef, 40);

  return (
    <div className="w-full" style={{ backgroundColor: theme.surface }}>
      <style>{`
        .hero-glow-u-a {
          position: absolute;
          width: 42rem;
          height: 42rem;
          top: 50%;
          left: 50%;
          margin: -21rem 0 0 -21rem;
          border-radius: 9999px;
          filter: blur(90px);
          opacity: 0.35;
          background: radial-gradient(circle, ${theme.accent}, transparent 65%);
          animation: glowUA 23s ease-in-out infinite;
        }
        @keyframes glowUA {
          0%   { transform: translate(-8rem, -6rem) scale(1); }
          35%  { transform: translate(9rem, -2rem) scale(1.1); }
          70%  { transform: translate(3rem, 8rem) scale(0.9); }
          100% { transform: translate(-8rem, -6rem) scale(1); }
        }
        .hero-glow-u-b {
          position: absolute;
          width: 28rem;
          height: 28rem;
          top: 50%;
          left: 50%;
          margin: -14rem 0 0 -14rem;
          border-radius: 9999px;
          filter: blur(95px);
          opacity: 0.26;
          background: radial-gradient(circle, ${theme.gold}, transparent 65%);
          animation: glowUB 29s ease-in-out infinite;
        }
        @keyframes glowUB {
          0%   { transform: translate(7rem, 5rem) scale(1); }
          40%  { transform: translate(-7rem, -3rem) scale(0.9); }
          75%  { transform: translate(-2rem, -8rem) scale(1.15); }
          100% { transform: translate(7rem, 5rem) scale(1); }
        }

        .shape-drift {
          animation-name: shapeDrift;
          animation-timing-function: ease-in-out;
          animation-iteration-count: infinite;
        }
        @keyframes shapeDrift {
          0%, 100% { transform: translateY(0) rotate(0deg); }
          50% { transform: translateY(-16px) rotate(8deg); }
        }

        .cursor-wander {
          animation: cursorWander 14s ease-in-out infinite;
        }
        @keyframes cursorWander {
          0%   { left: 20%; top: 30%; }
          20%  { left: 68%; top: 22%; }
          40%  { left: 78%; top: 62%; }
          60%  { left: 34%; top: 74%; }
          80%  { left: 14%; top: 48%; }
          100% { left: 20%; top: 30%; }
        }
        .cursor-pulse {
          width: 8px;
          height: 8px;
          left: 2px;
          top: 2px;
          border: 2px solid;
          animation: cursorPulse 2.8s ease-out infinite;
        }
        @keyframes cursorPulse {
          0%   { width: 8px; height: 8px; left: 2px; top: 2px; opacity: 0.7; }
          100% { width: 46px; height: 46px; left: -17px; top: -17px; opacity: 0; }
        }

        .hero-dots-u {
          position: absolute;
          inset: -10px;
          opacity: 0.05;
          background-image: radial-gradient(#FFFFFF 1px, transparent 1px);
          background-size: 30px 30px;
          animation: dotsDriftU 46s linear infinite;
        }
        @keyframes dotsDriftU {
          from { background-position: 0 0; }
          to { background-position: -300px 300px; }
        }

        .hero-vignette-u {
          position: absolute;
          inset: 0;
          background: radial-gradient(ellipse at center, transparent 25%, ${theme.heroBg} 85%);
        }

        .card-glow {
          pointer-events: none;
          position: absolute;
          top: -3rem;
          right: -3rem;
          width: 9rem;
          height: 9rem;
          border-radius: 9999px;
          filter: blur(30px);
          background: ${theme.accent};
          opacity: 0.08;
          transition: opacity 0.3s ease;
        }
        .group:hover .card-glow { opacity: 0.16; }

        @media (prefers-reduced-motion: reduce) {
          .hero-glow-u-a, .hero-glow-u-b, .shape-drift, .cursor-wander, .cursor-pulse, .hero-dots-u { animation: none !important; }
        }
      `}</style>

      {/* ================= HERO — design canvas bg, no card/device mock ================= */}
      <section
        ref={heroRef}
        className="relative overflow-hidden pt-44 pb-28 md:pt-56 md:pb-40"
        style={{ backgroundColor: theme.heroBg }}
      >
        <motion.div style={{ y: bgParallax }} className="absolute inset-0">
          <DesignCanvasField />
        </motion.div>

        <div className="relative z-10 max-w-4xl mx-auto px-6 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease: EASE }}
          >
            <Eyebrow light center>UI / UX Design</Eyebrow>
            <h1 className="text-4xl md:text-6xl font-extrabold leading-[1.08] tracking-tight text-white">
              <SplitText text="Design that's" className="text-white" />
              <br />
              <span style={{ color: theme.accent }}>intuitive & beautiful</span>
            </h1>
            <motion.p
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.55, ease: EASE }}
              className="mt-6 text-lg text-white/60 max-w-xl mx-auto"
            >
              Designing intuitive, beautiful, and conversion-focused digital
              experiences.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.7, ease: EASE }}
              className="mt-10 flex flex-wrap items-center justify-center gap-4"
            >
              <MagneticButton>
                <Link
                  to="/contact"
                  data-cursor="hover"
                  style={{ backgroundColor: theme.accent }}
                  className="group inline-flex items-center gap-2 text-white px-7 py-3.5 rounded-md font-semibold hover:brightness-110 transition-all"
                >
                  Start Design
                  <ArrowRight size={18} className="transition-transform group-hover:translate-x-1" />
                </Link>
              </MagneticButton>
              <MagneticButton strength={0.25}>
                <Link
                  to="/work"
                  data-cursor="hover"
                  className="inline-flex items-center gap-2 border border-white/25 text-white px-7 py-3.5 rounded-md font-medium hover:bg-white hover:text-[#221619] transition-colors"
                >
                  View Our Work
                </Link>
              </MagneticButton>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* ================= STATS ================= */}
      <section className="py-20" style={{ backgroundColor: theme.surface }}>
        <div className="max-w-6xl mx-auto px-6 grid md:grid-cols-4 gap-8 text-center">
          {stats.map((s, i) => (
            <motion.div
              key={s.t}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.1 }}
            >
              <h3 className="text-4xl font-extrabold" style={{ color: theme.accent }}>{s.n}</h3>
              <p className="mt-2" style={{ color: theme.inkMuted }}>{s.t}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* ================= PHILOSOPHY ================= */}
      <section className="py-24" style={{ backgroundColor: theme.surfaceAlt }}>
        <RevealSection className="max-w-4xl mx-auto px-6">
          <Eyebrow>Our Philosophy</Eyebrow>
          <h2 className="text-3xl md:text-4xl font-bold mb-6" style={{ color: theme.ink }}>
            Design Rooted in Real Behavior
          </h2>
          <p className="text-lg leading-relaxed" style={{ color: theme.inkMuted }}>
            We believe great design is not just how it looks, but how it
            works. Our UI/UX process is rooted in user psychology,
            accessibility, and business goals to deliver experiences users
            love.
          </p>
        </RevealSection>
      </section>

      {/* ================= SERVICES (dark band, interactive spotlight) ================= */}
      <section className="relative py-24 overflow-hidden" style={{ backgroundColor: theme.darkBand }}>
        <DotGrid light />
        <div
          className="pointer-events-none absolute top-0 right-0 w-[32rem] h-[32rem] rounded-full blur-3xl opacity-[0.12] translate-x-1/3 -translate-y-1/3 transition-colors duration-500"
          style={{ backgroundColor: serviceAccents[activeService] }}
        />

        <div className="max-w-7xl mx-auto px-6 relative z-10">
          <RevealSection className="max-w-2xl mb-16">
            <Eyebrow light>What We Offer</Eyebrow>
            <h2 className="text-4xl md:text-5xl font-extrabold leading-tight text-white">
              <SplitText text="UI / UX design" />{" "}
              <SplitText text="services" delay={0.2} className="inline" as="span" />
            </h2>
            <p className="mt-6 text-lg leading-relaxed text-white/60">
              User-focused, modern and scalable design work for digital
              products, end to end.
            </p>
          </RevealSection>

          <RevealSection
            delay={0.1}
            className="grid md:grid-cols-[280px_1fr] gap-4 md:gap-0 rounded-3xl border border-white/10 overflow-hidden"
          >
            <div className="flex md:flex-col overflow-x-auto md:overflow-visible" style={{ backgroundColor: "rgba(255,255,255,0.03)" }}>
              {services.map((item, i) => {
                const isActive = i === activeService;
                return (
                  <button
                    key={item.no}
                    data-cursor="hover"
                    onClick={() => setActiveService(i)}
                    onMouseEnter={() => setActiveService(i)}
                    className="relative flex items-center gap-3 text-left px-6 py-5 shrink-0 w-full transition-colors duration-300"
                    style={{ backgroundColor: isActive ? "rgba(255,255,255,0.05)" : "transparent" }}
                  >
                    <span
                      className="absolute left-0 top-0 bottom-0 w-[3px] transition-transform duration-300 origin-left"
                      style={{ backgroundColor: serviceAccents[i], transform: isActive ? "scaleX(1)" : "scaleX(0)" }}
                    />
                    <span
                      className="font-mono text-xs font-semibold transition-colors duration-300"
                      style={{ color: isActive ? serviceAccents[i] : "rgba(255,255,255,0.35)" }}
                    >
                      {item.no}
                    </span>
                    <span
                      className="text-sm font-semibold whitespace-nowrap transition-colors duration-300"
                      style={{ color: isActive ? "#FFFFFF" : "rgba(255,255,255,0.55)" }}
                    >
                      {item.title}
                    </span>
                  </button>
                );
              })}
            </div>

            <div className="relative p-10 md:p-14 min-h-[280px] flex items-center" style={{ backgroundColor: "rgba(255,255,255,0.02)" }}>
              <span className="pointer-events-none absolute -right-10 -bottom-10 opacity-[0.06]" style={{ color: "#FFFFFF" }}>
                {(() => {
                  const ActiveIcon = services[activeService].icon;
                  return <ActiveIcon size={220} strokeWidth={1} />;
                })()}
              </span>

              <AnimatePresence mode="wait">
                <motion.div
                  key={activeService}
                  initial={{ opacity: 0, y: 12 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -12 }}
                  transition={{ duration: 0.3, ease: "easeOut" }}
                  className="relative z-10 max-w-lg"
                >
                  <div
                    className="w-14 h-14 rounded-xl flex items-center justify-center mb-6"
                    style={{ backgroundColor: `${serviceAccents[activeService]}26`, color: serviceAccents[activeService] }}
                  >
                    {(() => {
                      const ActiveIcon = services[activeService].icon;
                      return <ActiveIcon size={26} />;
                    })()}
                  </div>
                  <h3 className="text-2xl md:text-3xl font-bold text-white">
                    {services[activeService].title}
                  </h3>
                  <p className="mt-4 text-white/60 leading-relaxed">
                    {services[activeService].desc}
                  </p>
                  <Link
                    to="/contact"
                    data-cursor="hover"
                    style={{ color: serviceAccents[activeService] }}
                    className="mt-6 inline-flex items-center gap-2 font-semibold hover:brightness-110 transition-all w-fit"
                  >
                    Get started
                    <ArrowUpRight size={16} />
                  </Link>
                </motion.div>
              </AnimatePresence>
            </div>
          </RevealSection>
        </div>
      </section>

      {/* ================= TOOLS (dark) ================= */}
      <section className="relative py-24 overflow-hidden" style={{ backgroundColor: theme.darkBandDeep }}>
        <DotGrid light />
        <div className="max-w-6xl mx-auto px-6 relative z-10">
          <RevealSection className="text-center mb-14">
            <Eyebrow light center>What We Design With</Eyebrow>
            <h2 className="text-3xl md:text-4xl font-extrabold text-white">Tools We Use</h2>
          </RevealSection>
          <motion.div
            variants={staggerContainer}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true }}
            className="grid grid-cols-2 md:grid-cols-6 gap-4 text-center"
          >
            {tools.map((tool) => (
              <motion.div
                key={tool}
                variants={staggerItem}
                whileHover={{ scale: 1.08, y: -4 }}
                data-cursor="hover"
                className="p-6 rounded-xl border font-semibold text-white transition-colors"
                style={{ backgroundColor: "rgba(255,255,255,0.04)", borderColor: "rgba(255,255,255,0.1)" }}
              >
                {tool}
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* ================= PROCESS (light) ================= */}
      <section className="py-24" style={{ backgroundColor: theme.surface }}>
        <div className="max-w-6xl mx-auto px-6">
          <RevealSection className="text-center mb-14">
            <Eyebrow center>How We Work</Eyebrow>
            <h2 className="text-3xl md:text-4xl font-extrabold" style={{ color: theme.ink }}>
              Our Design Process
            </h2>
          </RevealSection>
          <motion.div
            variants={staggerContainer}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true }}
            className="grid md:grid-cols-4 gap-6"
          >
            {process.map((item, i) => (
              <motion.div
                key={item.step}
                variants={staggerItem}
                whileHover={{ y: -6 }}
                data-cursor="hover"
                className="relative p-6 rounded-2xl border shadow-sm"
                style={{ backgroundColor: "#FFFFFF", borderColor: theme.border }}
              >
                <span
                  className="inline-flex items-center justify-center w-8 h-8 rounded-full text-xs font-bold mb-4"
                  style={{ backgroundColor: `${theme.accent}1A`, color: theme.accent }}
                >
                  {String(i + 1).padStart(2, "0")}
                </span>
                <h3 className="font-semibold mb-2" style={{ color: theme.ink }}>{item.step}</h3>
                <p className="text-sm" style={{ color: theme.inkMuted }}>{item.desc}</p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* ================= FAQ (light) ================= */}
      <section className="py-24" style={{ backgroundColor: theme.surfaceAlt }}>
        <div className="max-w-4xl mx-auto px-6">
          <RevealSection className="text-center mb-14">
            <Eyebrow center>Questions</Eyebrow>
            <h2 className="text-3xl md:text-4xl font-extrabold" style={{ color: theme.ink }}>FAQs</h2>
          </RevealSection>
          {faqs.map((item, i) => (
            <motion.div
              key={item.q}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.08 }}
              className="mb-5 p-6 rounded-xl border shadow-sm"
              style={{ backgroundColor: "#FFFFFF", borderColor: theme.border }}
            >
              <h3 className="font-semibold mb-2" style={{ color: theme.ink }}>{item.q}</h3>
              <p style={{ color: theme.inkMuted }}>{item.a}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* ================= CTA (dark) ================= */}
      <section
        className="relative py-28 overflow-hidden"
        style={{ background: `linear-gradient(160deg, ${theme.ctaBg} 0%, ${theme.ctaBgDeep} 100%)` }}
      >
        <DotGrid light />
        <div
          className="pointer-events-none absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[40rem] h-[40rem] rounded-full blur-3xl opacity-[0.14]"
          style={{ backgroundColor: theme.accent }}
        />
        <span
          aria-hidden="true"
          className="pointer-events-none select-none absolute -right-8 -top-8 opacity-[0.05]"
          style={{ color: "#FFFFFF" }}
        >
          <PenTool size={220} strokeWidth={1} />
        </span>

        <RevealSection className="relative z-10 max-w-4xl mx-auto px-6 text-center">
          <Eyebrow center light>Let's Get Designing</Eyebrow>
          <h2 className="text-4xl md:text-5xl font-extrabold leading-tight text-white">
            <SplitText text="Let's design something" />
            <span className="block">
              <span style={{ color: theme.accent }}>beautiful</span>
            </span>
          </h2>
          <p className="mt-6 text-lg text-white/60 max-w-xl mx-auto">
            Transform ideas into delightful user experiences.
          </p>
          <div className="mt-10 flex flex-wrap items-center justify-center gap-4">
            <MagneticButton>
              <Link
                to="/contact"
                data-cursor="hover"
                style={{ backgroundColor: theme.accent }}
                className="group inline-flex items-center gap-2 px-9 py-4 rounded-md text-white font-semibold text-lg shadow-lg shadow-black/30 hover:brightness-110 transition-all"
              >
                Start Design
                <ArrowRight size={20} className="transition-transform group-hover:translate-x-1" />
              </Link>
            </MagneticButton>
          </div>
        </RevealSection>
      </section>
    </div>
  );
}